import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Menu, X, User, LogOut } from 'lucide-react';

const Navigation = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getNavLinks = () => {
    if (!user) return [];

    switch (user.role) {
      case 'patient':
        return [
          { to: '/dashboard', label: 'My Dashboard' },
          { to: '/documents', label: 'My Documents' },
          { to: '/community', label: 'Community' },
          { to: '/settings', label: 'Settings' }
        ];
      case 'provider':
        return [
          { to: '/provider/patients', label: 'My Patients' },
          { to: '/documents', label: 'My Documents' },
          { to: '/community', label: 'Community' },
          { to: '/settings', label: 'Settings' }
        ];
      case 'admin':
        return [
          { to: '/admin/dashboard', label: 'Patient Census' },
          { to: '/admin/accounts', label: 'Account Management' },
          { to: '/community', label: 'Community' },
          { to: '/settings', label: 'Settings' }
        ];
      case 'employer':
        return [
          { to: '/employer/dashboard', label: 'Employer Dashboard' },
          { to: '/community', label: 'Community' },
          { to: '/settings', label: 'Settings' }
        ];
      default:
        return [];
    }
  };

  const navLinks = getNavLinks();

  if (!user) return null;

  return (
    <nav className="bg-white border-b border-slate-100 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-32">
          <div className="flex items-center space-x-8">
            <Link to="/" className="flex items-center space-x-2" data-testid="nav-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_virtualcareclinic/artifacts/5rgbdbiq_PILGRIM%20%281%29.png" 
                alt="Pilgrim Health" 
                className="h-28"
              />
            </Link>
            
            <div className="hidden md:flex space-x-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:text-cyan-700 hover:bg-cyan-50 transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="relative hidden md:block">
              <button
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                data-testid="profile-menu-button"
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors"
              >
                {user.profile_photo ? (
                  <img
                    src={user.profile_photo}
                    alt={`${user.first_name} ${user.last_name}`}
                    className="w-8 h-8 rounded-full object-cover border-2 border-cyan-100"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                    <User size={18} className="text-cyan-700" />
                  </div>
                )}
                <span className="text-sm font-medium text-slate-700">{user.first_name} {user.last_name}</span>
              </button>

              {profileMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-100 py-2">
                  <div className="px-4 py-2 border-b border-slate-100">
                    <p className="text-xs font-medium text-slate-500 uppercase">{user.role}</p>
                    <p className="text-sm text-slate-700 truncate">{user.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    data-testid="logout-button"
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                  >
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-slate-50"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden border-t border-slate-100 bg-white">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-cyan-50 hover:text-cyan-700"
              >
                {link.label}
              </Link>
            ))}
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50"
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;